import React, { useState } from 'react';
import StatusBadge from './StatusBadge';
import AuditTimelineRow from './AuditTimelineRow';

const IcoChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 4l4 4-4 4"/>
  </svg>
);
const IcoChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 6l4 4 4-4"/>
  </svg>
);

const getScoreClass = (score) => {
  if (score >= 85) return 'ts-good';
  if (score >= 70) return 'ts-blue';
  if (score >= 50) return 'ts-warn';
  return 'ts-red';
};

// ── Reusable premium badge ────────────────────────────────────────────────────
const PremiumBadge = ({ isPaid }) => isPaid ? (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 8px', borderRadius: 100,
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    border: '1px solid rgba(250,204,21,0.30)',
    boxShadow: '0 0 6px rgba(250,204,21,0.12), inset 0 1px 0 rgba(255,255,255,0.07)',
    position: 'relative', overflow: 'hidden',
  }}>
    <style>{`@keyframes paid-shimmer { 0%,100% { left:-60% } 50% { left:120% } }`}</style>
    <span style={{ position: 'absolute', top: 0, left: '-100%', width: '60%', height: '100%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)', animation: 'paid-shimmer 3s ease-in-out infinite', pointerEvents: 'none' }} />
    <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#facc15', boxShadow: '0 0 5px rgba(250,204,21,0.9)', flexShrink: 0 }} />
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M2 20h20M4 20L2 9l6 4 4-7 4 7 6-4-2 11H4z" stroke="#22c55e" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="2"  cy="9" r="1.5" fill="#22c55e"/>
      <circle cx="12" cy="2" r="1.5" fill="#22c55e"/>
      <circle cx="22" cy="9" r="1.5" fill="#22c55e"/>
    </svg>
    <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#ffffff' }}>PAID</span>
  </span>
) : (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 8px', borderRadius: 100,
    background: 'linear-gradient(135deg, #052e16 0%, #064e3b 50%, #065f46 100%)',
    border: '1px solid rgba(34,197,94,0.30)',
    boxShadow: '0 0 6px rgba(34,197,94,0.10), inset 0 1px 0 rgba(255,255,255,0.07)',
    position: 'relative', overflow: 'hidden',
  }}>
    <span style={{ position: 'absolute', top: 0, left: '-100%', width: '60%', height: '100%', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)', animation: 'paid-shimmer 3s ease-in-out infinite', pointerEvents: 'none' }} />
    <span style={{ width: 4, height: 4, borderRadius: '50%', background: '#4ade80', boxShadow: '0 0 5px rgba(74,222,128,0.9)', flexShrink: 0 }} />
    <svg width={10} height={10} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <rect x="3" y="11" width="18" height="11" rx="2" stroke="#4ade80" strokeWidth={2}/>
      <path d="M7 11V7a5 5 0 0 1 9.9-1" stroke="#4ade80" strokeWidth={2} strokeLinecap="round"/>
    </svg>
    <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.10em', textTransform: 'uppercase', color: '#ffffff' }}>FREE</span>
  </span>
);

const SubmissionsTable = ({ tableData, searchQuery, setSearchQuery, sortConfig, doSort, formatDisplayDate, getETA, onViewReport }) => {
  const [expandedRows, setExpandedRows] = useState([]);

  const toggleRowExpansion = (id) =>
    setExpandedRows(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const SI = ({ col }) => (
    <span style={{ opacity: sortConfig.key === col ? 1 : .3, marginLeft: 3, fontSize: 9 }}>
      {sortConfig.key === col ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  return (
    <div className="tbl-wrap">
      <div className="tbl-head-row">
        <div className="tbl-title">My Audit Reports</div>
        <div className="db-search">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}>
            <circle cx="7" cy="7" r="4.5"/><path d="M10.5 10.5L14 14"/>
          </svg>
          <input placeholder="Search by name or Request ID…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
      </div>

      {/* Desktop table */}
      <div className="desktop-table">
        <table className="db-table">
          <thead>
            <tr>
              <th style={{ width: 52, cursor: 'default', paddingLeft: 24 }}></th>
              <th onClick={() => doSort('presentationName')}>Presentation Name <SI col="presentationName" /></th>
              <th>Request ID</th>
              <th onClick={() => doSort('uploadDate')}>Upload Date <SI col="uploadDate" /></th>
              <th onClick={() => doSort('status')}>Status <SI col="status" /></th>
              <th onClick={() => doSort('decxScore')}>DecX Score <SI col="decxScore" /></th>
              <th style={{ cursor: 'default' }}>ETA</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map(r => {
              const isExpanded = expandedRows.includes(r.id);
              const scoreColorClass = getScoreClass(r.decxScore);
              const eta = getETA(r);
              return (
                <React.Fragment key={r.id}>
                  <tr className="data-row">
                    <td style={{ paddingLeft: 24, paddingRight: 8 }}>
                      <button className="expand-btn" onClick={() => toggleRowExpansion(r.id)}>
                        {isExpanded ? <IcoChevronDown /> : <IcoChevronRight />}
                      </button>
                    </td>
                    <td style={{ paddingLeft: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                            <polyline points="10 9 9 9 8 9"/>
                          </svg>
                        </div>
                        <div>
                          <div className="td-name">{r.presentationName}</div>
                          <div className="td-hint">{r.fileType || 'PPTX'}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="td-mono">{r.requestId || '—'}</div>
                      <div style={{ marginTop: 4 }}>
                        <PremiumBadge isPaid={r.isPaid} />
                      </div>
                    </td>
                    <td><span className="td-date">{formatDisplayDate(r.uploadDate)}</span></td>
                    <td><StatusBadge status={r.decxScore > 0 ? 'completed' : r.status} /></td>
                    <td>
                      {r.decxScore > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span className={`t-score ${scoreColorClass}`}>{r.decxScore}</span>
                          <span style={{ fontSize: 13, color: 'var(--text-4)', fontWeight: 400 }}>/100</span>
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-4)', fontSize: 13 }}>—</span>
                      )}
                    </td>
                    <td>
                      {r.decxScore > 0 ? (
                        <span style={{ color: 'var(--text-4)', fontSize: 13 }}>—</span>
                      ) : eta ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/>
                          </svg>
                          <span className="td-eta">{eta}</span>
                        </div>
                      ) : <span style={{ color: 'var(--text-4)', fontSize: 13 }}>—</span>}
                    </td>
                    <td>
                      {r.decxScore > 0 ? (
                        <button className="view-btn" onClick={() => onViewReport(r)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                          </svg>
                          View Results
                        </button>
                      ) : r.status === 'failed' ? (
                        <button className="retry-btn" onClick={() => window.location.href = '/submit'}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
                          </svg>
                          Retry
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="expand-row">
                      <td colSpan={8} style={{ padding: 0 }}>
                        <AuditTimelineRow item={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="mobile-cards">
        {tableData.map(r => {
          const isExpanded = expandedRows.includes(r.id);
          const scoreColorClass = getScoreClass(r.decxScore);
          const eta = getETA(r);
          return (
            <div key={r.id} className="mob-card">
              <div className="mob-card-top">
                <button onClick={() => toggleRowExpansion(r.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-4)', marginTop: 8, flexShrink: 0, display: 'flex', alignItems: 'center', transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--text-1)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--text-4)'}>
                  {isExpanded
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                  }
                </button>
                <div style={{ width: 40, height: 40, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mob-card-name">{r.presentationName}</div>
                  <div className="mob-card-sub">{r.fileType || 'PPTX'}</div>
                  {r.requestId && <div style={{ fontSize: 10, color: 'var(--text-4)', marginTop: 3 }}>{r.requestId}</div>}
                </div>
                <StatusBadge status={r.decxScore > 0 ? 'completed' : r.status} />
              </div>
              <div className="mob-card-grid">
                <div><div className="mob-card-kv-k">Upload Date</div><div className="mob-card-kv-v">{formatDisplayDate(r.uploadDate)}</div></div>
                <div>
                  <div className="mob-card-kv-k">DecX Score</div>
                  {r.decxScore > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <span className={`t-score ${scoreColorClass}`} style={{ fontSize: 20 }}>{r.decxScore}</span>
                      <span style={{ fontSize: 12, color: 'var(--text-4)' }}>/100</span>
                    </div>
                  ) : <span style={{ fontSize: 13, color: 'var(--text-4)' }}>—</span>}
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <PremiumBadge isPaid={r.isPaid} />
              </div>
              {!r.decxScore && eta && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/>
                  </svg>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>ETA: {eta}</span>
                </div>
              )}
              {isExpanded && <AuditTimelineRow item={r} />}
              {r.decxScore > 0 && (
                <button className="view-btn" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} onClick={() => onViewReport(r)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                  View Results
                </button>
              )}
              {r.status === 'failed' && (
                <button className="retry-btn" style={{ width: '100%', justifyContent: 'center', marginTop: 8 }} onClick={() => window.location.href = '/submit'}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10"/>
                  </svg>
                  Retry Upload
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SubmissionsTable;